import { useState, useEffect, useRef, useCallback } from "react";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../bootstrap/Toast";
import { FileApi, FileListEditorParams } from "./Editors";
import { DomEventService } from "../../../services/DomEventService";
import FilePortalService, {
  MultiuploadResultDataType
} from "../../../services/FilePortalService";
import FileService, {
  MultiuploadResultType
} from "../../../services/FileService";
import TooltipComponent from "../TooltipComponent";
import { FileItem } from "../../client/activity/components/NewComment";

type PortalTokenType = { token?: string };
export const MultiFileUpload = (
  params: FileListEditorParams & PortalTokenType
) => {
  const [showDrag, setShowDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<FileApi[]>(params.files ?? []);

  const dropRef = useRef<HTMLDivElement | null>(null);
  const { onFilesChange, fileDelete, variant, token = "" } = params;

  const downloadFile = params.downloadFile ?? FileService.download;
  const updateFiles = useCallback(
    (files: FileApi[]) => {
      setFiles([...files]);
      if (onFilesChange) {
        onFilesChange(files);
      }
    },
    [onFilesChange]
  );

  const dragLeaveTimeout = useRef<number>(0);

  useEffect(() => {
    const subscriptions = [
      DomEventService.GetBodyEvents("dragover").subscribe((x) => {
        if (x.dataTransfer?.types.includes("Files")) {
          x.preventDefault();
          clearTimeout(dragLeaveTimeout.current);
          setShowDrag(true);
        }
      }),
      DomEventService.GetBodyEvents("dragleave").subscribe((x) => {
        if (x.dataTransfer?.types.includes("Files")) {
          clearTimeout(dragLeaveTimeout.current);
          dragLeaveTimeout.current = setTimeout(
            () => setShowDrag(false),
            100,
            undefined
          );
        }
      }),
      DomEventService.GetBodyEvents("drop").subscribe((x) => {
        if (x.dataTransfer?.types.includes("Files")) {
          if (x.target === dropRef.current) {
            x.preventDefault();
            const files = [...x.dataTransfer.items]
              .map((item) =>
                item.kind === "file" ? item.getAsFile() : undefined
              )
              .filterFalsey();
            if (files.length === 0) {
              files.push(...x.dataTransfer.files);
            }
            onFileChange(files);
          }
          clearTimeout(dragLeaveTimeout.current);
          setShowDrag(false);
        }
      })
    ];
    return () => subscriptions.forEach((x) => x.unsubscribe());
  });

  const onFileChange = async (fileList: File[]) => {
    if (!fileList.length) {
      return;
    }

    const filesToUpload = [...fileList].filter((file) => {
      if (file.size > 50000000) {
        ToastService.showToast(
          TranslationService.translate.FileSizeError,
          undefined,
          "danger"
        );
        return false;
      }
      return true;
    });
    setLoading(true);
    const result = variant
      ? await FilePortalService.multiupload(filesToUpload, token)
      : await FileService.multiupload(filesToUpload);
    if (result instanceof Error) {
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      setLoading(false);
      return false;
    } else {
      setLoading(false);
      const filesData = variant
        ? (result as MultiuploadResultDataType)?.data?.files.map((x) => ({
            id: x.id,
            name: x.filename
          }))
        : (result as MultiuploadResultType)?.files?.map((x) => ({
            id: x.id,
            name: x.filename
          }));
      updateFiles([...files, ...filesData]);
    }
  };

  const removeFile = useCallback(
    (id: string) => {
      updateFiles([...files.filter((x) => x.id !== id)]);
      if (fileDelete) {
        fileDelete(id);
      }
    },
    [fileDelete, files, updateFiles]
  );

  return (
    <>
      {params.canEdit && (
        <>
          {showDrag ? (
            <div ref={dropRef} className="drop-files-box">
              {TranslationService.translate.DropYourFilesHere}
            </div>
          ) : (
            <label className="d-flex align-items-center has-pointer removeOnPrint">
              <input
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => onFileChange([...(e.target.files ?? [])])}
              />
              <div style={{ width: 45, textAlign: "center" }}>
                <TooltipComponent
                  title={TranslationService.translate.SelectOrDragAFile}
                >
                  <i className="far fa-fw fa-paperclip activity-icon-color my-2" />
                </TooltipComponent>
              </div>
              <div className="files-container col mb-1 gap-1">
                {files && files?.length > 0 ? (
                  files.map((x: any) => (
                    <FileItem key={x.id} {...{ removeFile, downloadFile, x }} />
                  ))
                ) : (
                  <>{TranslationService.translate.SelectOrDragAFile}</>
                )}
                {loading && (
                  <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                )}
              </div>
            </label>
          )}
        </>
      )}
    </>
  );
};
